import {
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonToolbar,
  type RefresherEventDetail,
  useIonRouter,
  useIonViewWillEnter,
} from "@ionic/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { calendarOutline, helpCircleOutline } from "ionicons/icons";
import { ListSkeleton } from "@/components/atoms/skeleton";
import { Content } from "@/components/content";
import { EmptyState } from "@/components/molecules/empty-state";
import { InfoModal } from "@/components/molecules/info-modal";
import { OrganizationDropdown } from "@/components/molecules/organization/organization-dropdown";
import { DiscoverCalendar } from "@/components/molecules/project/discover-calendar";
import { AgendaListItem } from "@/components/molecules/shift/agenda-list-item";
import { CompletedShiftsView } from "@/components/molecules/shift/completed-shifts-view";
import { Toolbar } from "@/components/toolbar";
import { dayKeyOf } from "@/lib/discover-day-state";
import { type DayKey, discoverFetchWindow, monthOf, todayKey } from "@/lib/discover-window";
import { useDiscoverDayStates } from "@/lib/hooks/use-discover-day-states";
import { useUpcomingAgenda } from "@/lib/hooks/use-upcoming-agenda";
import { useOutstandingHours } from "@/lib/query/shift/use-outstanding-hours";
import { Routes } from "@/lib/router";

type Tab = "agenda" | "completed";

/** Eén lege verzameling, zodat de kalenderstippen niet elke render opnieuw uitgerekend worden. */
const EMPTY_DAYS: ReadonlySet<DayKey> = new Set();
type AgendaFilter = "all" | "confirmed" | "pending";

const FILTER_OPTIONS = ["all", "confirmed", "pending"] as const;

/**
 * Hoe ver ónder de kop een rij mag staan en nog steeds de dag is waar je naar kijkt.
 *
 * De kalender leest de lijst en niet andersom, dus hij heeft een lijn nodig om aan af te lezen. Die
 * lijn is de onderrand van de kop -- alles daarboven zit erachter en wordt niet gelezen -- plus wat
 * speling, zodat een rij die er net onder is geschoven de kalender niet meteen een week verder
 * zet.
 *
 * Zelfde getal en dezelfde reden als op Ontdek.
 */
const READING_BAND = 24;

/** Even lang glijden als op Ontdek, zodat de twee kalenders hetzelfde aanvoelen. */
const JUMP_DURATION = 400;

/**
 * Diensten.
 *
 * Two tabs, split by direction: what is still ahead of you, and what is behind you. The status of
 * an item lives on its card, not in the navigation — see `use-upcoming-agenda` for why.
 *
 * This replaces the feature-flagged three-versus-four sub-tab split, which rendered two different
 * products out of one file and moved the selected tab out from under the user when the flag
 * resolved.
 */
export function UpcomingShiftsReworkPage() {
  const { t } = useTranslation();
  const router = useIonRouter();
  const infoModalRef = useRef<HTMLIonModalElement>(null);

  const [selectedTab, setSelectedTab] = useState<Tab>("agenda");

  // Eén klok voor het hele scherm: `today` en het venster van de kalender horen niet uit elkaar
  // te lopen doordat ze allebei hun eigen `new Date()` pakken.
  const now = useMemo(() => new Date(), []);
  const today = useMemo(() => todayKey(now), [now]);
  const [anchor, setAnchor] = useState<DayKey>(today);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const agendaListRef = useRef<HTMLIonListElement>(null);
  const contentRef = useRef<HTMLIonContentElement>(null);
  const headerRef = useRef<HTMLIonHeaderElement>(null);
  /** Waar terwijl een sprong glijdt, zodat de kalender zijn eigen scrollen niet terugleest. */
  const isJumping = useRef(false);

  /**
   * Hoeveel van de bovenkant van de scroller de kop afdekt.
   *
   * De scroller ligt ónder de kop -- `.inner-scroll` staat absoluut op `top: calc(var(--offset-top)
   *
   * - -1)`-- dus een rij die naar`0` gescrold wordt parkeert achter het glas, en de sprong lijkt
   *   niets gedaan te hebben.
   *
   * Gemeten en niet opgeschreven, want het is niet één getal: de kalender in de kop is dichtgeklapt
   * één rij en open zes, en dat verandert onder de duim van de lezer.
   */
  const coveredHeight = () => headerRef.current?.offsetHeight ?? 0;
  const [filter, setFilter] = useState<AgendaFilter>("all");

  const agendaQuery = useUpcomingAgenda();
  // The badge comes from its own bounded query rather than the first page of a list, so it
  // is right for someone with a backlog. CompletedShiftsView owns the list itself.
  const outstandingHours = useOutstandingHours();

  const visibleAgenda = agendaQuery.agenda.filter((item) => {
    if (filter === "confirmed") {
      return item.status === "confirmed";
    }
    if (filter === "pending") {
      return item.status !== "confirmed";
    }
    return true;
  });

  const refetchAll = async () => {
    await Promise.all([agendaQuery.refetch(), outstandingHours.refetch(), dayStates.refetch()]);
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await refetchAll();
    event.detail.complete();
  };

  useIonViewWillEnter(() => {
    void refetchAll();
  });

  // Only the agenda paginates here; CompletedShiftsView brings its own infinite scroll.
  const handleInfiniteScroll = (ev: CustomEvent) => agendaQuery.loadMore(ev);

  const infoSections =
    selectedTab === "completed"
      ? [
          { content: t("routes:upcoming-shifts.info.completed.description") },
          {
            title: t("routes:upcoming-shifts.info.completed.section1-title"),
            content: t("routes:upcoming-shifts.info.completed.section1-content"),
          },
        ]
      : [
          { content: t("routes:upcoming-shifts.info.agenda.description") },
          {
            title: t("routes:upcoming-shifts.info.agenda.section1-title"),
            content: t("routes:upcoming-shifts.info.agenda.section1-content"),
          },
          {
            title: t("routes:upcoming-shifts.info.agenda.section2-title"),
            content: t("routes:upcoming-shifts.info.agenda.section2-content"),
          },
        ];

  /**
   * Dezelfde stippen als op Ontdek, en uit dezelfde bron.
   *
   * Ze kwamen eerst uit `visibleAgenda`, de lijst die hier al op het scherm staat. Dat leek zuinig
   * -- geen extra bevragingen -- maar die lijst is gepagineerd en gefilterd: hij begint met tien
   * rijen en laadt de rest pas als je scrollt. De kalender toonde daardoor alleen de dagen die je
   * toevallig al voorbij was gescrold, en zag er verder compleet uit. Sta je over twee weken
   * ingepland en heb je genoeg werk daarvoor, dan is die dag gewoon leeg.
   *
   * Precies waar de docstring van `use-discover-day-states` voor waarschuwt, en waarvoor die hook
   * bestaat: één begrensde bevraging over het hele venster dat de kalender kan bereiken, niet de
   * eerste bladzijde van een lijst.
   *
   * `availableDays` blijft leeg -- "hier is nog werk te vinden" is een vraag voor Ontdek, niet voor
   * je eigen agenda.
   */
  const window = useMemo(() => discoverFetchWindow(monthOf(anchor), now), [anchor, now]);

  const dayStates = useDiscoverDayStates({ window, availableDays: EMPTY_DAYS });

  /**
   * Een dag aantikken schuift de lijst, hij snijdt hem niet weg.
   *
   * Er is niet op elke dag iets, dus tikken op een lege dag brengt je naar de eerstvolgende dag die
   * wél iets heeft. Zo eindigt geen enkele tik op "hier staat niets" -- dezelfde afspraak als de
   * kalender op Ontdek maakt.
   */
  const jumpToDay = async (day: DayKey) => {
    setAnchor(day);

    // De lijst staat op datum, dus de eerste rij die niet vóór deze dag ligt is de juiste. Uit de
    // dom lezen en niet uit een bijgehouden map: dan kan er ook niets in achterblijven als het
    // filter de lijst verandert.
    const rows = agendaListRef.current?.querySelectorAll<HTMLElement>("[data-day]") ?? [];
    const target = [...rows].find((row) => (row.dataset.day ?? "") >= day);
    const content = contentRef.current;
    if (!target || !content) {
      return;
    }

    const scroller = await content.getScrollElement();
    const top =
      target.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop -
      coveredHeight();

    // Glijden, en de kalender stilhouden terwijl het glijdt: het scrollen zet de dag waar de
    // kalender op staat, dus een sprong over drie maanden zou hem onderweg door elke week heen
    // laten flikkeren en trillend aankomen.
    isJumping.current = true;
    try {
      await content.scrollToPoint(0, Math.max(0, top), JUMP_DURATION);
    } finally {
      isJumping.current = false;
    }
  };

  /**
   * De andere kant op: scrollen zet de dag waar de kalender op staat.
   *
   * Dit ontbrak hier, terwijl Ontdek het wel deed -- de kalender bleef staan waar je hem voor het
   * laatst had aangetikt terwijl je door de lijst liep. De waarnemer bepaalt alleen wannéér er
   * gekeken wordt; het antwoord wordt opnieuw uitgerekend over alle rijen, want een lijst met
   * kruisende rijen vertelt je welke de lijn passeerden, niet onder welke je bent geëindigd.
   */
  useEffect(() => {
    const content = contentRef.current;
    const list = agendaListRef.current;
    if (selectedTab !== "agenda" || !content || !list) {
      return;
    }

    let observer: IntersectionObserver | undefined;
    let cancelled = false;

    void content.getScrollElement().then((scroller) => {
      if (cancelled) {
        return;
      }

      const settle = () => {
        if (isJumping.current) {
          return;
        }
        const rows = Array.from(list.querySelectorAll<HTMLElement>("[data-day]"));
        if (rows.length === 0) {
          return;
        }

        const scrollerTop = scroller.getBoundingClientRect().top;
        const line = coveredHeight() + READING_BAND;
        let current = rows[0].dataset.day;
        for (const row of rows) {
          if (row.getBoundingClientRect().top - scrollerTop > line) {
            break;
          }
          current = row.dataset.day;
        }

        if (current) {
          setAnchor(current);
        }
      };

      // Geen `rootMargin` om dit tot een band te versmallen: die moet tegen de hoogte van de
      // scroller uitgedrukt worden, en die hoogte is nul zolang de pagina nog niet is opgemaakt --
      // dan wordt de marge `-Infinity%` en gooit de constructor. Wakker worden bij elke rij die de
      // scroller in of uit schuift kost een handvol extra aanroepen van een functie die rechthoeken
      // leest en verder niets.
      observer = new IntersectionObserver(settle, { root: scroller });
      list.querySelectorAll<HTMLElement>("[data-day]").forEach((row) => observer?.observe(row));
    });

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [selectedTab, visibleAgenda]);

  // Never claim an empty agenda before we know — a cold open would otherwise tell someone
  // they have no work for as long as the first request takes.
  const showAgendaEmptyState = !agendaQuery.isLoading && visibleAgenda.length === 0;

  return (
    <IonPage>
      <IonHeader ref={headerRef}>
        <Toolbar className="ion-no-border plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),theme(spacing.4))]">
          {/* Het vraagteken stond hier, geklemd tussen de titel en de organisatie. Die regel gaat
              over waar je bent en bij wie; wat de statussen betekenen hoort bij de statussen. Het
              staat nu rechts op de filterregel, waar het uitlegt wat je daar ziet staan. */}
          <div className="relative flex items-center gap-1 py-0">
            <h1 className="text-2xl font-semibold leading-[1.2] text-txt-primary">
              {t("routes:upcoming-shifts.title")}
            </h1>
            <OrganizationDropdown />
          </div>
        </Toolbar>

        {/* De kalender staat in de kop en niet als `sticky` blok in de lijst.

            Het was allebei een poging tot hetzelfde -- een besturing die blijft staan terwijl je
            eronder leest -- maar `sticky` deed het hier niet, en het glas werd er lelijk van: twee
            halfdoorzichtige panelen boven elkaar tekenen ieder hun eigen vervaging, en op de naad
            zie je dat. In de kop is het één vlak, en "blijven staan" is geen truc meer maar de
            gewone gang van zaken.

            Wat dit kostte was de reden om het eerder níét te doen: Ionic meet de hoogte van de kop
            bij het laden en verder alleen bij een `resize`, dus een kalender die van week naar
            maand klapt verstopte de bovenkant van de lijst. Die meting wordt nu bijgestuurd -- zie
            de schildwacht in `components/content.tsx`.

            De inspring is dezelfde als op Ontdek: `px-4` hier in de kop, en niets extra's op de
            lijst eronder -- die staat al binnen de zestien pixels van `ion-padding`. Diensten deed
            er zijn eigen `px-6` bovenop (en `px-10` hier, om dat te volgen), en stond daarmee ruim
            twee keer zo ver van de rand als het scherm ernaast. Twee lijsten met dezelfde rijen en
            twee verschillende marges leest als twee verschillende apps. */}
        {selectedTab === "agenda" && (
          <div className="px-4 pb-2 plt-desktop:sm:px-[max(calc(calc(100vw-640px)/2),theme(spacing.4))]">
            <DiscoverCalendar
              anchor={anchor}
              onJump={jumpToDay}
              states={dayStates.states}
              expanded={calendarExpanded}
              onExpandedChange={setCalendarExpanded}
              today={today}
            />
          </div>
        )}
      </IonHeader>

      <Content ref={contentRef} scrollUnderChrome>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent refreshingSpinner="circles" />
        </IonRefresher>

        <IonToolbar className="ion-no-padding border-b border-solid border-bordercolor-primary bg-surface-primary">
          <div className="flex w-full items-center justify-between gap-2">
            <TabButton
              isActive={selectedTab === "agenda"}
              label={t("routes:upcoming-shifts.agenda-tab")}
              onClick={() => setSelectedTab("agenda")}
            />
            <TabButton
              isActive={selectedTab === "completed"}
              label={t("routes:upcoming-shifts.completed-tab")}
              badge={outstandingHours.count}
              onClick={() => setSelectedTab("completed")}
            />
          </div>
        </IonToolbar>

        {selectedTab === "agenda" && (
          <>
            <div className="flex items-center gap-2 pt-4">
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setFilter(option)}
                  className={`rounded-full border border-solid px-3.5 py-1.5 text-sm font-medium transition-opacity active:opacity-70 ${
                    filter === option
                      ? "border-txt-primary bg-txt-primary text-txt-inverted"
                      : "border-bordercolor-primary text-txt-secondary"
                  }`}
                >
                  {t(`routes:upcoming-shifts.filter.${option}`)}
                </button>
              ))}

              <button
                type="button"
                onClick={() => infoModalRef.current?.present()}
                aria-label={t("routes:upcoming-shifts.info-label")}
                className="touch-target ml-auto rounded-lg p-2 transition-opacity active:opacity-70"
              >
                <IonIcon icon={helpCircleOutline} className="size-6 text-txt-tertiary" />
              </button>
            </div>

            {/* `showAgendaEmptyState` hield de lege staat al tegen tijdens het laden -- terecht,
                "je staat nergens ingepland" is het verkeerde antwoord op een lijst die nog
                onderweg is. Maar wat er dan overbleef was een lijst zonder items, en dus een
                leeg vlak. Laden is niet hetzelfde als leeg én niet hetzelfde als niets. */}
            {agendaQuery.isLoading ? (
              <ListSkeleton count={4} className="py-6" />
            ) : showAgendaEmptyState ? (
              <div className="flex flex-col items-center gap-4 py-8">
                <EmptyState
                  icon={calendarOutline}
                  title={t("routes:upcoming-shifts.empty-agenda.title")}
                  description={t("routes:upcoming-shifts.empty-agenda.description")}
                />
                <button
                  type="button"
                  onClick={() => router.push(Routes.Projects(), "forward")}
                  className="rounded-xl border-2 border-solid border-button-primary bg-button-primary px-5 py-3 text-md font-semibold leading-[1.5] text-txt-inverted"
                >
                  {t("routes:upcoming-shifts.empty-agenda.cta")}
                </button>
              </div>
            ) : (
              <IonList ref={agendaListRef} className="flex flex-col gap-6 py-6">
                {visibleAgenda.map((item) => (
                  <div key={item.key} data-day={dayKeyOf(item.startAt)}>
                    <AgendaListItem item={item} />
                  </div>
                ))}
              </IonList>
            )}
          </>
        )}

        {selectedTab === "completed" && (
          <div className="py-6">
            <CompletedShiftsView />
          </div>
        )}

        {selectedTab === "agenda" && (
          <IonInfiniteScroll
            onIonInfinite={handleInfiniteScroll}
            disabled={!agendaQuery.hasNextPage}
          >
            <IonInfiniteScrollContent loadingText={t("common:loading")} loadingSpinner="bubbles" />
          </IonInfiniteScroll>
        )}
      </Content>

      <InfoModal
        modalRef={infoModalRef}
        title={
          selectedTab === "completed"
            ? t("routes:upcoming-shifts.completed-tab")
            : t("routes:upcoming-shifts.agenda-tab")
        }
        sections={infoSections}
      />
    </IonPage>
  );
}

function TabButton({
  isActive,
  label,
  badge,
  onClick,
}: {
  isActive: boolean;
  label: string;
  /**
   * Optioneel: het agenda-tabblad droeg het aantal openstaande uitnodigingen, en die zijn er niet
   * meer.
   */
  badge?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex flex-1 flex-col items-center p-0 after:absolute after:bottom-0 after:h-1 after:w-full after:rounded-[32px] ${
        isActive ? "after:bg-txt-primary" : "after:bg-transparent"
      }`}
    >
      <div className="flex items-center gap-2 px-0 py-2">
        <span
          className={`text-md font-medium leading-[1.5] ${
            isActive ? "text-txt-primary" : "text-txt-secondary"
          }`}
        >
          {label}
        </span>
        {badge !== undefined && badge > 0 && (
          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-txt-primary">
            <span className="text-xs font-semibold leading-none text-txt-inverted">{badge}</span>
          </div>
        )}
      </div>
    </button>
  );
}
